import type { TFunction } from "i18next";

import type { DoctorSpecialty, Role } from "@/lib/api/users";

/**
 * "Effective role" — what the UI shows in the role dropdown.
 * Maps to (role, specialty) pair on submit:
 *   - simple roles → role only
 *   - doctor specialties → role="doctor", specialty=<value>
 */
export type EffectiveRoleValue =
  | "admin"
  | "registrar"
  | "doctor"
  | "lab_tech"
  | "chief_doctor"
  | "medical_commission"
  | `doctor:${DoctorSpecialty}`;

const SPECIALTIES: DoctorSpecialty[] = [
  "therapist",
  "neurologist",
  "ophthalmologist",
  "ent",
  "surgeon",
  "gynecologist",
  "urologist",
  "dentist",
  "psychiatrist_narcologist",
  "dermatovenerologist",
  "cardiologist",
  "radiologist",
  "laboratory",
];

export function buildRoleOptions(t: TFunction): {
  group: string;
  items: { value: EffectiveRoleValue; label: string }[];
}[] {
  return [
    {
      group: t("users.role_group.staff"),
      items: [
        { value: "admin", label: t("users.roles.admin") },
        { value: "registrar", label: t("users.roles.registrar") },
        { value: "lab_tech", label: t("users.roles.lab_tech") },
        { value: "chief_doctor", label: t("users.roles.chief_doctor") },
        { value: "medical_commission", label: t("users.roles.medical_commission") },
        { value: "doctor", label: t("users.roles.doctor_general") },
      ],
    },
    {
      group: t("users.role_group.specialists"),
      items: SPECIALTIES.map((s) => ({
        value: `doctor:${s}` as EffectiveRoleValue,
        label: t(`users.specialties.${s}`),
      })),
    },
  ];
}

export function effectiveValueFor(
  role: Role | "",
  specialty: DoctorSpecialty | null | "",
): EffectiveRoleValue | "" {
  if (!role) return "";
  if (role === "doctor" && specialty) return `doctor:${specialty}` as EffectiveRoleValue;
  return role as EffectiveRoleValue;
}

export function roleAndSpecialtyFrom(
  effective: EffectiveRoleValue | "",
): { role: Role | ""; specialty: DoctorSpecialty | null } {
  if (!effective) return { role: "", specialty: null };
  if (effective.startsWith("doctor:")) {
    return {
      role: "doctor",
      specialty: effective.slice("doctor:".length) as DoctorSpecialty,
    };
  }
  return { role: effective as Role, specialty: null };
}
